import type {ReactElement} from 'react';
import React, {useEffect, useRef} from 'react';
import {CUSTOM_CONTROLS_WRAPPER} from '@Components/poster-editor/components/custom-item-controls/custom-item-controls.types';
import {CustomOutlineBoxes} from '@Components/poster-editor/components/custom-item-controls/components/custom-outline-boxes/custom-outline-boxes';
import {SingleItemCustomControls} from '@Components/poster-editor/components/custom-item-controls/components/single-item-custom-controls/single-item-custom-controls';
import {ActiveSelectionCustomControls} from '@Components/poster-editor/components/custom-item-controls/components/active-selection-custom-controls/active-selection-custom-controls';
import {useSingleActivePosterItem} from '@Hooks/poster-editor/useSingleActivePosterItem';
import {useAppSelector} from '@/hooks';
import {getHoveredItem} from '@Components/poster-editor/poster-editor-reducer';
import {POSTER_ELEMENT_CONTAINER} from '@Components/poster-whiteboard/poster-whiteboard.types';

export function CustomItemControls(): ReactElement | null {
  const hoveredItemObj = useAppSelector(getHoveredItem);
  const isPosterPlaying = useAppSelector((state) => state.posterEditor.isPlaying);
  const isTouchGestureBeingApplied = useAppSelector((state) => state.posterEditor.isTouchGestureBeingApplied);
  const activeItem = useSingleActivePosterItem();
  const hoveredItem = hoveredItemObj ? window.posterEditor?.whiteboard?.getItemForId(hoveredItemObj.uid) : undefined;

  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    const posterElement: HTMLElement | null = document.querySelector(`.${POSTER_ELEMENT_CONTAINER}`);

    if (!posterElement || !wrapper) {
      return;
    }

    const updateFixedPositionOfControlsWrapper = (): void => {
      const {top, left} = posterElement.getBoundingClientRect();
      wrapper.style.top = `${top}px`;
      wrapper.style.left = `${left}px`;
    };

    updateFixedPositionOfControlsWrapper();

    let animationFrameId: number;
    const loopPositionTracking = (): void => {
      updateFixedPositionOfControlsWrapper();
      animationFrameId = requestAnimationFrame(loopPositionTracking);
    };

    loopPositionTracking();

    return (): void => {
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  if (hoveredItem?.introAnimation.animation.isAnimationPlaying || (isPosterPlaying && activeItem?.hasAnimation())) {
    return null;
  }

  if (isTouchGestureBeingApplied) {
    return null;
  }

  return (
    <div
      ref={wrapperRef}
      style={{
        position: 'fixed',
        zIndex: 1,
      }}
      key={CUSTOM_CONTROLS_WRAPPER}
      id={CUSTOM_CONTROLS_WRAPPER}
    >
      <CustomOutlineBoxes />
      <SingleItemCustomControls />
      <ActiveSelectionCustomControls />
    </div>
  );
}
